import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  getSchemas,
  getSchema,
  createSchema,
  updateSchema,
  deleteSchema,
} from "../api/metadataSchemaApi";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { Hero, EmptyState } from "../components/adminSurface/AdminSurface";
import { SchemaArt } from "../components/adminSurface/heroArt";
import "./MetadataSchemas.css";

const FIELD_TYPES = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "decimal", label: "Decimal" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean" },
];

function Alert({ type = "info", children, onClose }) {
  const bg =
    type === "error" ? "#ffe9e9" : type === "success" ? "#e9f7ef" : "#f6f8ff";
  const color =
    type === "error" ? "#8b0000" : type === "success" ? "#116622" : "#264b9a";
  return (
    <div
      style={{
        background: bg,
        color,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${
          type === "error"
            ? "#f3c6c6"
            : type === "success"
            ? "#b8e6c8"
            : "#d0dff5"
        }`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 600 }}>{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px 8px",
            marginLeft: 12,
            fontSize: 18,
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export default function MetadataSchemas() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingSchema, setEditingSchema] = useState(null);
  const [viewingSchema, setViewingSchema] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  const [formData, setFormData] = useState({
    schemaName: "",
    description: "",
    isActive: true,
    fields: [],
  });

  const [newField, setNewField] = useState({
    fieldName: "",
    displayName: "",
    fieldType: "string",
    isRequired: false,
    minLength: "",
    maxLength: "",
    pattern: "",
    allowedValues: "",
    validationMessage: "",
    isActive: true,
  });

  const [showRegexBuilder, setShowRegexBuilder] = useState(false);
  const [regexTestValue, setRegexTestValue] = useState("");
  const [showJsonBuilder, setShowJsonBuilder] = useState(false);
  const [jsonBuilderValues, setJsonBuilderValues] = useState([]);
  const [newJsonValue, setNewJsonValue] = useState("");

  const JSON_TEMPLATES = [
    {
      name: "Document Status",
      values: ["Draft", "Submitted", "Approved", "Rejected"],
    },
    { name: "Priority Levels", values: ["Low", "Medium", "High", "Critical"] },
    { name: "Yes/No", values: ["Yes", "No"] },
    { name: "True/False", values: ["True", "False"] },
    { name: "Active/Inactive", values: ["Active", "Inactive"] },
    { name: "Quarters", values: ["Q1", "Q2", "Q3", "Q4"] },
    {
      name: "Departments",
      values: ["Sales", "Marketing", "Engineering", "HR", "Finance"],
    },
  ];

  const REGEX_TEMPLATES = [
    {
      name: "Email",
      pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
      description: "Valid email address",
    },
    {
      name: "Phone (US)",
      pattern: "^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$",
      description: "US phone number",
    },
    {
      name: "Invoice Number",
      pattern: "^INV-[0-9]{5,8}$",
      description: "INV- followed by 5-8 digits",
    },
    {
      name: "Date (YYYY-MM-DD)",
      pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$",
      description: "YYYY-MM-DD format",
    },
    {
      name: "Alphanumeric",
      pattern: "^[a-zA-Z0-9]+$",
      description: "Letters and numbers only",
    },
    {
      name: "Letters Only",
      pattern: "^[a-zA-Z]+$",
      description: "Only alphabetic characters",
    },
    {
      name: "Numbers Only",
      pattern: "^[0-9]+$",
      description: "Only numeric characters",
    },
    {
      name: "Postal Code (US)",
      pattern: "^[0-9]{5}(-[0-9]{4})?$",
      description: "5 digits or ZIP+4",
    },
    {
      name: "URL",
      pattern: "^https?://[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(/.*)?$",
      description: "HTTP/HTTPS URL",
    },
    {
      name: "Credit Card",
      pattern: "^[0-9]{4}-?[0-9]{4}-?[0-9]{4}-?[0-9]{4}$",
      description: "16 digit card number",
    },
  ];

  const applyRegexTemplate = (template) => {
    setNewField({ ...newField, pattern: template.pattern });
    setShowRegexBuilder(false);
  };

  const testRegexPattern = () => {
    if (!newField.pattern || !regexTestValue) return null;
    try {
      const regex = new RegExp(newField.pattern);
      return regex.test(regexTestValue);
    } catch (e) {
      return "Invalid regex";
    }
  };

  const openJsonBuilder = () => {
    // Parse existing values if any
    if (newField.allowedValues) {
      try {
        const parsed = JSON.parse(newField.allowedValues);
        if (Array.isArray(parsed)) {
          setJsonBuilderValues(parsed);
        }
      } catch (e) {
        setJsonBuilderValues([]);
      }
    } else {
      setJsonBuilderValues([]);
    }
    setShowJsonBuilder(true);
  };

  const applyJsonTemplate = (template) => {
    setJsonBuilderValues(template.values);
    const jsonString = JSON.stringify(template.values);
    setNewField({ ...newField, allowedValues: jsonString });
  };

  const addJsonValue = () => {
    if (newJsonValue.trim()) {
      const updated = [...jsonBuilderValues, newJsonValue.trim()];
      setJsonBuilderValues(updated);
      setNewField({ ...newField, allowedValues: JSON.stringify(updated) });
      setNewJsonValue("");
    }
  };

  const removeJsonValue = (index) => {
    const updated = jsonBuilderValues.filter((_, i) => i !== index);
    setJsonBuilderValues(updated);
    setNewField({ ...newField, allowedValues: JSON.stringify(updated) });
  };

  const moveJsonValueUp = (index) => {
    if (index === 0) return;
    const updated = [...jsonBuilderValues];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setJsonBuilderValues(updated);
    setNewField({ ...newField, allowedValues: JSON.stringify(updated) });
  };

  const moveJsonValueDown = (index) => {
    if (index === jsonBuilderValues.length - 1) return;
    const updated = [...jsonBuilderValues];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setJsonBuilderValues(updated);
    setNewField({ ...newField, allowedValues: JSON.stringify(updated) });
  };

  const fetchSchemas = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSchemas(token, false);
      setSchemas(Array.isArray(data) ? data : []);
    } catch (err) {
      const errorMsg = friendlyMessageForApiError(err);
      // Don't show 404 errors as they just mean no schemas exist yet
      if (!errorMsg.includes("404") && !errorMsg.includes("Not Found")) {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemas();
  }, [token]);

  const resetForm = () => {
    setFormData({
      schemaName: "",
      description: "",
      isActive: true,
      fields: [],
    });
    setNewField({
      fieldName: "",
      displayName: "",
      fieldType: "string",
      isRequired: false,
      minLength: "",
      maxLength: "",
      pattern: "",
      allowedValues: "",
      validationMessage: "",
      isActive: true,
    });
    setEditingSchema(null);
    setShowFormModal(false);
  };

  const openCreateForm = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEditForm = async (schemaId) => {
    setActionInProgress(true);
    try {
      const schema = await getSchema(token, schemaId);
      setFormData({
        schemaName: schema.schemaName || "",
        description: schema.description || "",
        isActive: schema.isActive !== false,
        fields: (schema.fields || []).map((f) => ({
          ...f,
          minLength: f.minLength || "",
          maxLength: f.maxLength || "",
          pattern: f.pattern || "",
          allowedValues: f.allowedValues || "",
          validationMessage: f.validationMessage || "",
        })),
      });
      setEditingSchema(schema);
      setShowFormModal(true);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const viewSchema = async (schemaId) => {
    setActionInProgress(true);
    try {
      const schema = await getSchema(token, schemaId);
      setViewingSchema(schema);
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionInProgress(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        schemaName: formData.schemaName,
        description: formData.description,
        isActive: formData.isActive,
        fields: formData.fields.map((f, idx) => ({
          id: f.id || null,
          fieldName: f.fieldName,
          displayName: f.displayName,
          fieldType: f.fieldType,
          isRequired: f.isRequired,
          minLength: f.minLength ? parseInt(f.minLength) : null,
          maxLength: f.maxLength ? parseInt(f.maxLength) : null,
          pattern: f.pattern || null,
          allowedValues: f.allowedValues || null,
          defaultValue: f.defaultValue || null,
          validationMessage: f.validationMessage || null,
          fieldOrder: idx,
          isActive: f.isActive !== false,
        })),
      };

      if (editingSchema) {
        await updateSchema(token, editingSchema.id, payload);
        setSuccess(t("Schema updated successfully"));
      } else {
        await createSchema(token, payload);
        setSuccess(t("Schema created successfully"));
      }

      resetForm();
      fetchSchemas();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleDelete = async (schemaId) => {
    if (!window.confirm(t("Are you sure you want to delete this schema?")))
      return;

    setActionInProgress(true);
    setError("");
    setSuccess("");
    try {
      await deleteSchema(token, schemaId);
      setSuccess(t("Schema deleted successfully"));
      fetchSchemas();
    } catch (err) {
      setError(friendlyMessageForApiError(err));
    } finally {
      setActionInProgress(false);
    }
  };

  const addField = () => {
    if (!newField.fieldName.trim()) {
      setError(t("Field name is required"));
      return;
    }

    if (formData.fields.some((f) => f.fieldName === newField.fieldName)) {
      setError(t("Field name must be unique"));
      return;
    }

    setFormData({
      ...formData,
      fields: [...formData.fields, { ...newField }],
    });

    setNewField({
      fieldName: "",
      displayName: "",
      fieldType: "string",
      isRequired: false,
      minLength: "",
      maxLength: "",
      pattern: "",
      allowedValues: "",
      validationMessage: "",
      isActive: true,
    });
  };

  const removeField = (index) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter((_, i) => i !== index),
    });
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    const newFields = [...formData.fields];
    [newFields[index - 1], newFields[index]] = [
      newFields[index],
      newFields[index - 1],
    ];
    setFormData({ ...formData, fields: newFields });
  };

  const moveFieldDown = (index) => {
    if (index === formData.fields.length - 1) return;
    const newFields = [...formData.fields];
    [newFields[index], newFields[index + 1]] = [
      newFields[index + 1],
      newFields[index],
    ];
    setFormData({ ...formData, fields: newFields });
  };

  return (
    <div className="schemas-page">
      <HeaderBar />
      <NavBar />
      <div className="schemas-content">
        {error && (
          <Alert type="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert type="success" onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Hero
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
              strokeLinecap="round" strokeLinejoin="round" style={{ color: "#fff" }}>
              <rect x="4" y="3" width="16" height="18" rx="2" />
              <line x1="8" y1="8" x2="16" y2="8" />
              <line x1="8" y1="12" x2="16" y2="12" />
              <line x1="8" y1="16" x2="13" y2="16" />
            </svg>
          }
          title={t("admin.schemas", { defaultValue: "Metadata Schemas" })}
          subtitle={t("Define the required metadata fields for each document type")}
          logo={<SchemaArt />}
          actions={
            <button onClick={openCreateForm} className="asf-hero__btn">
              + {t("Create Schema")}
            </button>
          }
        />

        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        ) : schemas.length === 0 ? (
          <div className="asf-tablecard">
            <EmptyState
              icon="📋"
              title={t("No schemas found")}
              hint={t("Create your first metadata schema to define required fields for documents")}
            />
            <div style={{ textAlign: "center", paddingBottom: 48, marginTop: -18 }}>
              <button onClick={openCreateForm} className="asf-cta">
                + {t("Create Schema")}
              </button>
            </div>
          </div>
        ) : (
          <div className="schemas-list-card">
            <table className="schemas-table">
              <thead>
                <tr>
                  <th>{t("Schema Name")}</th>
                  <th>{t("Description")}</th>
                  <th>{t("Fields")}</th>
                  <th>{t("Created By")}</th>
                  <th>{t("Created At")}</th>
                  <th>{t("Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {schemas.map((schema) => (
                  <tr key={schema.id}>
                    <td>
                      <span className="schema-name">{schema.schemaName}</span>
                    </td>
                    <td>{schema.description || "-"}</td>
                    <td>
                      <span className="field-count-badge">
                        {schema.fields?.length || 0} fields
                      </span>
                    </td>
                    <td>{schema.createdBy || "-"}</td>
                    <td>
                      {schema.createdAt
                        ? new Date(schema.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <div className="schema-actions">
                        <button
                          onClick={() => viewSchema(schema.id)}
                          className="btn-icon"
                          title={t("View")}
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => openEditForm(schema.id)}
                          className="btn-icon"
                          title={t("Edit")}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(schema.id)}
                          className="btn-icon"
                          title={t("Delete")}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* View Schema Modal */}
        {viewingSchema && (
          <div className="modal-overlay" onClick={() => setViewingSchema(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{viewingSchema.schemaName}</h2>
                <button
                  onClick={() => setViewingSchema(null)}
                  className="btn-icon"
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: 16 }}>
                  <strong>{t("Description")}:</strong>{" "}
                  {viewingSchema.description || "-"}
                </p>
                <p style={{ marginBottom: 16 }}>
                  <strong>{t("Status")}:</strong>{" "}
                  {viewingSchema.isActive ? t("Active") : t("Inactive")}
                </p>
                <p style={{ marginBottom: 24 }}>
                  <strong>{t("Created")}:</strong> {viewingSchema.createdBy} on{" "}
                  {viewingSchema.createdAt
                    ? new Date(viewingSchema.createdAt).toLocaleString()
                    : "-"}
                </p>

                <h3 style={{ marginBottom: 12 }}>{t("Fields")}</h3>
                {viewingSchema.fields && viewingSchema.fields.length > 0 ? (
                  <table className="schemas-table">
                    <thead>
                      <tr>
                        <th>{t("Field Name")}</th>
                        <th>{t("Display Name")}</th>
                        <th>{t("Type")}</th>
                        <th>{t("Required")}</th>
                        <th>{t("Validation")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingSchema.fields.map((field, idx) => (
                        <tr key={idx}>
                          <td>
                            <code>{field.fieldName}</code>
                          </td>
                          <td>{field.displayName || "-"}</td>
                          <td>{field.fieldType}</td>
                          <td>{field.isRequired ? "✓" : "-"}</td>
                          <td style={{ fontSize: 12 }}>
                            {field.minLength && `Min: ${field.minLength} `}
                            {field.maxLength && `Max: ${field.maxLength} `}
                            {field.pattern && `Pattern `}
                            {field.allowedValues && `Enum `}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>{t("No fields defined")}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Schema Modal */}
        {showFormModal && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingSchema ? t("Edit Schema") : t("Create Schema")}</h2>
                <button onClick={resetForm} className="btn-icon">
                  ✕
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>{t("Schema Name")} *</label>
                    <input
                      type="text"
                      value={formData.schemaName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          schemaName: e.target.value,
                        })
                      }
                      required
                      className="form-control"
                      placeholder="e.g., InvoiceSchema"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t("Description")}</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="form-control"
                      rows="3"
                      placeholder="Describe the purpose of this schema"
                    />
                  </div>

                  <div className="form-group">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            isActive: e.target.checked,
                          })
                        }
                      />
                      {t("Active")}
                    </label>
                  </div>

                  <div className="form-section">
                    <h3>
                      📝 {t("Fields")} ({formData.fields.length})
                    </h3>

                    {formData.fields.length > 0 && (
                      <div className="fields-list">
                        {formData.fields.map((field, idx) => (
                          <div key={idx} className="field-item">
                            <div className="field-info">
                              <div className="field-name-display">
                                <code>{field.fieldName}</code>
                                {field.isRequired && (
                                  <span
                                    style={{
                                      color: "#dc3545",
                                      marginLeft: 4,
                                    }}
                                  >
                                    *
                                  </span>
                                )}
                              </div>
                              <div className="field-meta">
                                {field.displayName && `${field.displayName} • `}
                                {field.fieldType}
                                {field.minLength &&
                                  ` • Min: ${field.minLength}`}
                                {field.maxLength &&
                                  ` • Max: ${field.maxLength}`}
                              </div>
                            </div>
                            <div className="field-actions">
                              <button
                                type="button"
                                onClick={() => moveFieldUp(idx)}
                                className="btn-icon"
                                disabled={idx === 0}
                                title={t("Move up")}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                onClick={() => moveFieldDown(idx)}
                                className="btn-icon"
                                disabled={idx === formData.fields.length - 1}
                                title={t("Move down")}
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                onClick={() => removeField(idx)}
                                className="btn-icon"
                                style={{ color: "#dc3545" }}
                                title={t("Remove")}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="add-field-card">
                      <h4 style={{ marginBottom: 12, fontSize: 14 }}>
                        + {t("Add Field")}
                      </h4>
                      <div className="add-field-form">
                        <div className="form-group">
                          <label style={{ fontSize: 13 }}>
                            {t("Field Name")} *
                          </label>
                          <input
                            type="text"
                            value={newField.fieldName}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                fieldName: e.target.value,
                              })
                            }
                            className="form-control"
                            placeholder="InvoiceNumber"
                          />
                        </div>
                        <div className="form-group">
                          <label style={{ fontSize: 13 }}>
                            {t("Display Name")}
                          </label>
                          <input
                            type="text"
                            value={newField.displayName}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                displayName: e.target.value,
                              })
                            }
                            className="form-control"
                            placeholder="Invoice #"
                          />
                        </div>
                        <div className="form-group">
                          <label style={{ fontSize: 13 }}>{t("Type")}</label>
                          <select
                            value={newField.fieldType}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                fieldType: e.target.value,
                              })
                            }
                            className="form-control"
                          >
                            {FIELD_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group-inline">
                          <input
                            type="checkbox"
                            checked={newField.isRequired}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                isRequired: e.target.checked,
                              })
                            }
                          />
                          <label style={{ fontSize: 13, margin: 0 }}>
                            {t("Required")}
                          </label>
                        </div>
                        <div className="form-group">
                          <label style={{ fontSize: 13 }}>
                            {t("Min Length")}
                          </label>
                          <input
                            type="number"
                            value={newField.minLength}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                minLength: e.target.value,
                              })
                            }
                            className="form-control"
                          />
                        </div>
                        <div className="form-group">
                          <label style={{ fontSize: 13 }}>
                            {t("Max Length")}
                          </label>
                          <input
                            type="number"
                            value={newField.maxLength}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                maxLength: e.target.value,
                              })
                            }
                            className="form-control"
                          />
                        </div>
                        <div
                          className="form-group"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          <label style={{ fontSize: 13 }}>
                            {t("Pattern (Regex)")}
                          </label>
                          <div style={{ display: "flex", gap: 8 }}>
                            <input
                              type="text"
                              value={newField.pattern}
                              onChange={(e) =>
                                setNewField({
                                  ...newField,
                                  pattern: e.target.value,
                                })
                              }
                              className="form-control"
                              placeholder="^INV-[0-9]{5}$"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowRegexBuilder(!showRegexBuilder)
                              }
                              className="btn btn-secondary"
                              style={{
                                whiteSpace: "nowrap",
                                padding: "8px 16px",
                              }}
                            >
                              🔧 {t("Pattern Builder")}
                            </button>
                          </div>
                          {showRegexBuilder && (
                            <div className="regex-builder-popup">
                              <h4 style={{ marginBottom: 12, fontSize: 14 }}>
                                {t("Common Patterns")}
                              </h4>
                              <div className="regex-templates">
                                {REGEX_TEMPLATES.map((template, idx) => (
                                  <div
                                    key={idx}
                                    className="regex-template-item"
                                    onClick={() => applyRegexTemplate(template)}
                                  >
                                    <div className="regex-template-name">
                                      {template.name}
                                    </div>
                                    <div className="regex-template-desc">
                                      {template.description}
                                    </div>
                                    <code className="regex-template-pattern">
                                      {template.pattern}
                                    </code>
                                  </div>
                                ))}
                              </div>
                              {newField.pattern && (
                                <div className="regex-tester">
                                  <h4 style={{ marginBottom: 8, fontSize: 14 }}>
                                    {t("Test Pattern")}
                                  </h4>
                                  <input
                                    type="text"
                                    value={regexTestValue}
                                    onChange={(e) =>
                                      setRegexTestValue(e.target.value)
                                    }
                                    className="form-control"
                                    placeholder={t("Enter test value")}
                                    style={{ marginBottom: 8 }}
                                  />
                                  {regexTestValue && (
                                    <div
                                      className={`regex-test-result ${
                                        testRegexPattern() === true
                                          ? "valid"
                                          : testRegexPattern() === false
                                          ? "invalid"
                                          : "error"
                                      }`}
                                    >
                                      {testRegexPattern() === true
                                        ? "✓ Match"
                                        : testRegexPattern() === false
                                        ? "✗ No match"
                                        : "⚠ Invalid regex"}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div
                          className="form-group"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          <label style={{ fontSize: 13 }}>
                            {t("Allowed Values (JSON array)")}
                          </label>
                          <div style={{ display: "flex", gap: 8 }}>
                            <input
                              type="text"
                              value={newField.allowedValues}
                              onChange={(e) =>
                                setNewField({
                                  ...newField,
                                  allowedValues: e.target.value,
                                })
                              }
                              className="form-control"
                              placeholder='["Draft","Submitted","Approved"]'
                            />
                            <button
                              type="button"
                              onClick={openJsonBuilder}
                              className="btn btn-secondary"
                              style={{
                                whiteSpace: "nowrap",
                                padding: "8px 16px",
                              }}
                            >
                              📝 {t("Value Builder")}
                            </button>
                          </div>
                          {showJsonBuilder && (
                            <div className="regex-builder-popup">
                              <h4 style={{ marginBottom: 12, fontSize: 14 }}>
                                {t("Quick Templates")}
                              </h4>
                              <div className="regex-templates">
                                {JSON_TEMPLATES.map((template, idx) => (
                                  <div
                                    key={idx}
                                    className="regex-template-item"
                                    onClick={() => applyJsonTemplate(template)}
                                  >
                                    <div className="regex-template-name">
                                      {template.name}
                                    </div>
                                    <div className="regex-template-desc">
                                      {template.values.join(", ")}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div
                                style={{
                                  borderTop: "1px solid #dee2e6",
                                  paddingTop: 16,
                                  marginTop: 16,
                                }}
                              >
                                <h4 style={{ marginBottom: 12, fontSize: 14 }}>
                                  {t("Build Custom List")}
                                </h4>

                                {jsonBuilderValues.length > 0 && (
                                  <div className="json-values-list">
                                    {jsonBuilderValues.map((value, idx) => (
                                      <div
                                        key={idx}
                                        className="json-value-item"
                                      >
                                        <span className="json-value-text">
                                          {value}
                                        </span>
                                        <div className="json-value-actions">
                                          <button
                                            type="button"
                                            onClick={() => moveJsonValueUp(idx)}
                                            className="btn-icon"
                                            disabled={idx === 0}
                                            title={t("Move up")}
                                          >
                                            ↑
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              moveJsonValueDown(idx)
                                            }
                                            className="btn-icon"
                                            disabled={
                                              idx ===
                                              jsonBuilderValues.length - 1
                                            }
                                            title={t("Move down")}
                                          >
                                            ↓
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => removeJsonValue(idx)}
                                            className="btn-icon"
                                            style={{ color: "#dc3545" }}
                                            title={t("Remove")}
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    marginTop: 12,
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={newJsonValue}
                                    onChange={(e) =>
                                      setNewJsonValue(e.target.value)
                                    }
                                    onKeyPress={(e) =>
                                      e.key === "Enter" &&
                                      (e.preventDefault(), addJsonValue())
                                    }
                                    className="form-control"
                                    placeholder={t("Add new value")}
                                  />
                                  <button
                                    type="button"
                                    onClick={addJsonValue}
                                    className="btn btn-secondary"
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    + {t("Add")}
                                  </button>
                                </div>

                                {jsonBuilderValues.length > 0 && (
                                  <div style={{ marginTop: 12 }}>
                                    <button
                                      type="button"
                                      onClick={() => setShowJsonBuilder(false)}
                                      className="btn btn-primary"
                                      style={{ width: "100%" }}
                                    >
                                      ✓ {t("Done")} ({jsonBuilderValues.length}{" "}
                                      {t("values")})
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div
                          className="form-group"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          <label style={{ fontSize: 13 }}>
                            {t("Validation Message")}
                          </label>
                          <input
                            type="text"
                            value={newField.validationMessage}
                            onChange={(e) =>
                              setNewField({
                                ...newField,
                                validationMessage: e.target.value,
                              })
                            }
                            className="form-control"
                            placeholder={t("Custom error message")}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={addField}
                        className="btn btn-secondary"
                        style={{ marginTop: 12 }}
                      >
                        + {t("Add Field")}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn btn-secondary"
                    disabled={actionInProgress}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={actionInProgress || formData.fields.length === 0}
                  >
                    {actionInProgress
                      ? t("common.saving")
                      : editingSchema
                      ? t("Update Schema")
                      : t("Create Schema")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
